import { useEffect, useState } from "react";
import { Bell, BellOff, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  permissionState,
  currentSubscription,
  enablePush,
  suggestionRecentlyDismissed,
  dismissSuggestion,
} from "../lib/push";

/**
 * Smart suggestion banner shown once per 7 days when:
 *   - Push is supported
 *   - Permission is "default" (never asked)
 *   - User has been on the dashboard for at least a few seconds
 *   - User hasn't dismissed recently
 */
export default function PushSuggestionBanner({ delayMs = 8000 }) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (permissionState() !== "default") return;
    if (suggestionRecentlyDismissed()) return;

    let cancelled = false;
    const decide = async () => {
      const existing = await currentSubscription();
      if (existing || cancelled) return;
      const handle = setTimeout(() => { if (!cancelled) setVisible(true); }, delayMs);
      return () => clearTimeout(handle);
    };
    const cleanup = decide();
    return () => { cancelled = true; cleanup?.then?.((fn) => fn && fn()); };
  }, [delayMs]);

  if (!visible) return null;

  const onDismiss = () => {
    dismissSuggestion();
    setVisible(false);
  };

  const onEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Notifications enabled. We'll only ping you when it matters.");
      setVisible(false);
    } catch (e) {
      toast.error(e?.message || "Couldn't enable notifications.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="ts-card p-5 mb-6 relative border-[#C9A84C]/40 animate-fade-in"
      data-testid="push-suggestion-banner"
    >
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-[#9A9590] hover:text-[#F0EDE8]"
        data-testid="push-suggestion-dismiss"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-10 h-10 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/30 flex items-center justify-center">
          <Bell size={18} className="text-[#C9A84C]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline">Stay close to the table</div>
          <h3 className="font-serif text-lg text-[#F0EDE8] mt-1.5 leading-snug">
            Get pinged when a deal moves.
          </h3>
          <p className="text-sm text-[#9A9590] mt-1.5 leading-relaxed">
            New opportunities in your sector, comments on your deal, action items waiting on you —
            on your lock screen, even when the app is closed.
          </p>
          <div className="mt-4 flex gap-3 flex-wrap">
            <button
              onClick={onEnable}
              disabled={busy}
              className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="push-suggestion-enable"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
              {busy ? "Enabling…" : "Enable notifications"}
            </button>
            <button
              onClick={onDismiss}
              className="text-xs uppercase tracking-[0.25em] text-[#9A9590] hover:text-[#F0EDE8] transition self-center inline-flex items-center gap-1"
              data-testid="push-suggestion-later"
            >
              <BellOff size={12} /> Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
